/**
 * Portal links in the navigation (docs/integration-portal-link-proposal.md §4.4, §6 in the SVN
 * repo). A portal is a page Inistate publishes for a workspace on e.inistate.com; a member opens it
 * from the side menu in a NEW TAB, through the app's own route /#/portal/{id}, which asks the server
 * for a launch address and moves on to it. Nothing here reads or keeps a portal's tokens.
 *
 * Store-free on purpose: both shells and the settings page use these, and the specs test them
 * without the app around them.
 */

/**
 * The icons an admin can give a portal, by the name the server stores (at most 40 characters).
 * Every one is registered in main.js's library.add, or it would render nothing. The first is the
 * default for a new portal and the fallback for a name this list does not know.
 */
export const PORTAL_ICONS = Object.freeze([
  { name: "window-maximize", prefix: "far", label: "Window" },
  { name: "chart-bar", prefix: "fas", label: "Bar chart" },
  { name: "chart-line", prefix: "fas", label: "Line chart" },
  { name: "chart-pie", prefix: "fas", label: "Pie chart" },
  { name: "table", prefix: "fas", label: "Table" },
  { name: "tasks", prefix: "fas", label: "Tasks" },
  { name: "file-alt", prefix: "fas", label: "Document" },
  { name: "clipboard", prefix: "far", label: "Clipboard" },
  { name: "calendar", prefix: "far", label: "Calendar" },
  { name: "users", prefix: "fas", label: "People" },
  { name: "shopping-bag", prefix: "fas", label: "Shop" },
  { name: "building", prefix: "far", label: "Building" },
  { name: "map-marked-alt", prefix: "fas", label: "Map" },
  { name: "lightbulb", prefix: "fas", label: "Idea" },
]);

export const DEFAULT_PORTAL_ICON = PORTAL_ICONS[0].name;

/**
 * How to draw a portal's icon: { fa: [prefix, name] } for a name in PORTAL_ICONS, { emoji } for a
 * short emoji (the API takes any string, and the design's first sketch used one), else the generic
 * window icon.
 */
export function portalIcon(icon) {
  const name = typeof icon === "string" ? icon.trim() : "";
  const known = PORTAL_ICONS.find((x) => x.name === name);
  if (known) return { fa: [known.prefix, known.name] };
  // An emoji has no ASCII letters or digits in it; a word that is not in the list is a typo, not a picture.
  if (name && name.length <= 16 && !/[A-Za-z0-9]/.test(name)) return { emoji: name };
  return { fa: [PORTAL_ICONS[0].prefix, PORTAL_ICONS[0].name] };
}

/**
 * The app's own address of a portal: <origin + path>#/portal/{id}?ws={workspace}. Also its shareable
 * link. The workspace rides along so a click from a tab on an older workspace still launches in the
 * portal's own workspace (the wsId cookie holds whichever workspace was opened last, in any tab).
 */
export function portalHref(id, loc, workspaceId) {
  const where = loc || (typeof window !== "undefined" ? window.location : null);
  const base = where ? String(where.origin || "") + String(where.pathname || "/") : "/";
  const ws = /^\d{1,10}$/.test(String(workspaceId || "")) ? "?ws=" + String(workspaceId) : "";
  return base + "#/portal/" + encodeURIComponent(String(id)) + ws;
}

/**
 * The portals a shell lists on the web: the bootstrap's `workspace.portals` (already narrowed to
 * what this member may see), less those not shown on the web. An older server sends no list at all,
 * which is the same as none. Nothing in Run As or a demo: a portal opens as the person who is
 * signed in, never as the person being impersonated (PL-13), and the new tab would not carry Run As.
 *
 * @param {object} workspace  clientStore.state.workspace
 * @param {object} [context]  { runAs: boolean }
 * @returns {Array<{ id, name, icon, label }>}
 */
export function webPortals(workspace, context) {
  const ws = workspace || {};
  const ctx = context || {};
  if (ctx.runAs || ws.demoId) return [];
  const list = Array.isArray(ws.portals) ? ws.portals : [];
  return list
    .filter((p) => p && p.id != null && p.id !== "" && p.platforms && p.platforms.web === true)
    .map((p) => {
      const name = String(p.name || "").trim() || "Portal";
      return {
        id: String(p.id),
        name,
        icon: portalIcon(p.icon),
        label: name + ", opens in a new tab",
      };
    });
}

/**
 * The bootstrap's shape of the admin list (GET api/portal), for the admin's own menu right after a
 * change in Settings: the switched-on portals in their order, as the server's VisibleForAsync builds
 * them for a workspace admin, who sees the admins-only ones too.
 */
export function bootstrapPortals(adminPortals) {
  const list = Array.isArray(adminPortals) ? adminPortals.slice() : [];
  return list
    .filter((p) => p && p.enabled)
    .sort((a, b) => (Number(a.sequence) || 0) - (Number(b.sequence) || 0))
    .map((p) => ({
      id: p.id,
      name: p.name,
      icon: p.icon || null,
      platforms: { web: Boolean(p.showOnWeb), app: Boolean(p.showOnApp) },
    }));
}

export default { PORTAL_ICONS, DEFAULT_PORTAL_ICON, portalIcon, portalHref, webPortals, bootstrapPortals };
